import { type PropsWithChildren, createContext, memo, useContext, useMemo, useState } from 'react';

interface CdaSectionSelectionState {
  selectedSectionIds: Array<string>;
  selectAllSectionIds: () => void;
  clearSelectedSectionIds: () => void;
  selectSectionId: (sectionId: string) => void;
  deselectSectionId: (sectionId: string) => void;
}

const CdaSectionSelectionContext = createContext<CdaSectionSelectionState | undefined>(undefined);

interface CdaSectionSelectionProviderProps extends PropsWithChildren {
  sectionIds: Array<string>;
}

export const CdaSectionSelectionProvider = memo(
  ({ sectionIds, children }: CdaSectionSelectionProviderProps) => {
    const [selectedSectionIds, setSelectedSectionIds] = useState<Array<string>>([]);

    const state: CdaSectionSelectionState = useMemo(
      () => ({
        selectedSectionIds,
        selectAllSectionIds: () => setSelectedSectionIds(sectionIds),
        clearSelectedSectionIds: () => setSelectedSectionIds([]),
        selectSectionId: (sectionId) =>
          setSelectedSectionIds((selectedIds) =>
            selectedIds.includes(sectionId) ? selectedIds : [...selectedIds, sectionId],
          ),
        deselectSectionId: (sectionId) =>
          setSelectedSectionIds((selectedIds) =>
            selectedIds.filter((selectedId) => selectedId !== sectionId),
          ),
      }),
      [sectionIds, selectedSectionIds],
    );

    return (
      <CdaSectionSelectionContext.Provider value={state}>
        {children}
      </CdaSectionSelectionContext.Provider>
    );
  },
);

export const useCdaSectionSelectionContext = () => {
  const context = useContext(CdaSectionSelectionContext);

  if (!context) {
    throw new Error(
      'useCdaSectionSelectionContext must be used inside a CdaSectionSelectionProvider',
    );
  }

  return context;
};
